import { generateAvailableEnsembleSizes, minimumPurchaseAmount } from '@models/util/product.util'
import { isLearnPcPreview } from '@utils/page-routes'

import PurchasedProduct from './PurchasedProduct'
import TitleMetadata from './TitleMetadata'
import Price, { Currency } from './commerce/Price'

import type { ClassProperties } from '../types/utils'
import type Product from '@models/Product'
import type TitlePermissions from '@models/TitlePermissions'
import type Session from '@utils/Session'

/* Interface for a Title class (properties only) */
export type TitleType = ClassProperties<Title>

export enum TitleModelType {
  EE = 'ee',
  DAM_ASSET = 'dam',
  CONTENT_LIBRARY = 'cl',
  COMMUNITY = 'community',
  UNKNOWN = 'unknown',
}

// this is used, for instance, in the author section of the TitleDetails panel
export type Author = {
  id?: string
  external_id: string
  username: string
  followers?: number
  avatar_uri?: string
  homepage_uri?: string
}

export type ContributorWithRole = {
  name: string
  role: string
}

export type Seller = {
  id: string
  avatar: string
  follower_count: number
  name: string
  homepage_uri: string
  following: boolean
}

export default class Title {
  static readonly Type = TitleModelType

  uuid: string
  title: string
  metadata: TitleMetadata
  embed_url?: string
  external_embed_url?: string
  views?: number
  favorites?: number
  favorited?: boolean
  author?: Author
  soundcheck_exercise_id?: string
  permissions?: TitlePermissions
  title_type?: TitleModelType
  complimentary?: boolean
  seller_avatar_url?: string
  seller?: Seller
  product?: Product
  purchased_product?: PurchasedProduct

  constructor(props: TitleType | Title) {
    this.uuid = props.uuid
    this.title = props.title
    this.metadata = new TitleMetadata(props.metadata)
    this.embed_url = props?.embed_url
    this.external_embed_url = props?.external_embed_url
    this.views = props?.views || 0
    this.favorites = props?.favorites || 0
    this.favorited = !!props?.favorited
    this.author = props?.author
    this.soundcheck_exercise_id = props?.soundcheck_exercise_id
    this.title_type = props?.title_type ?? Title.Type.UNKNOWN
    this.complimentary = !!props?.complimentary
    this.permissions = props?.permissions
    this.seller_avatar_url = props?.seller_avatar_url
    this.seller = props?.seller
    if (props.product) {
      this.product = props.product
    }
    if (props.purchased_product) {
      this.purchased_product = new PurchasedProduct(props.purchased_product)
    }
  }

  formattedUnitPrice(): string {
    if (!this.product?.price) return ''
    const price = new Price({
      priceInCents: this.product.price ?? 0,
      currency: this.product.currency ?? Currency.USD,
    })
    return price.getFormattedSubTotal()
  }

  formattedPrice(): string {
    if (!this.product?.price) return ''
    const price = new Price({
      priceInCents: (this.product.price ?? 0) * minimumPurchaseAmount(this.product),
      currency: this.product.currency ?? Currency.USD,
    })
    return price.getFormattedSubTotal()
  }

  /**
   * Whether this title is a user generated title
   */
  isUserGen(): boolean {
    return this.metadata.usergen
  }

  /**
   * Whether or not the current logged in user owns this title
   */
  isOwnedByUser(): boolean {
    return (
      typeof this.purchased_product?.purchased_quantity === 'number' &&
      this.purchased_product?.purchased_quantity > 0
    )
  }

  /**
   * Whether or not the current logged in user is an authorized performer for this title
   */
  isAuthorizedForUser(): boolean {
    return this.permissions?.authorized_performer ?? false
  }

  /**
   * Whether the given user is the seller of this title
   * @param userId The user's ID
   */
  isSeller(userId?: string): boolean {
    if (!userId) {
      return false
    }
    return this.seller?.id === userId
  }

  /** Whether or not this title is an EE title */
  isEE(): boolean {
    return this.title_type === Title.Type.EE
  }

  isCommunity(): boolean {
    return this.title_type === Title.Type.COMMUNITY
  }

  /** Where or not this title originated from some type of content library */
  isContentTitle(): boolean {
    return this.title_type === Title.Type.EE || this.title_type === Title.Type.CONTENT_LIBRARY
  }

  /**
   * Whether the price for this title should be shown to the current user
   * @param session
   */
  shouldShowPrice(session: Session): boolean {
    // Don't show if no price available
    if (!this.product?.price) return false

    // Don't show price if in a learn community
    if (!session.isDefaultCommunity() || isLearnPcPreview()) return false

    // Show price if title is user-generated
    if (this.isUserGen()) return true

    // Show price if anonymous or basic
    if (!session.isLoggedIn() || !session.hasPremiumFeatures()) return true

    // Otherwise, do not show price
    return false
  }

  noMoreEnsemblesAvailable() {
    if (!this.product) return true
    const purchasedQuantity = this.purchased_product?.purchased_quantity || 0

    // when ensemble_size_max is null we have a 21+ ensemble size,
    // a purchase quantity of 1 is the max that can be purchased
    if (this.product?.ensemble_size_max == null) {
      return purchasedQuantity > 0
    }

    const availableEnsembleSizes = generateAvailableEnsembleSizes(this.product, purchasedQuantity)
    // the first item could be 0 or there could be no availableEnsembleSizes
    return !availableEnsembleSizes[0]
  }
}
